import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    InputAdornment,
    InputLabel,
    ListItemText,
    ListSubheader,
    MenuItem,
    MenuList,
    OutlinedInput,
    Popover,
    TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DealGroupForSelector, serviceLabel } from './services-tab.helpers';

interface ServiceSearchFieldHandle {
    reset: () => void;
    focus: () => void;
}

const ServiceSearchField = forwardRef<ServiceSearchFieldHandle, { onSearch: (q: string) => void; disabled?: boolean }>(
    function ServiceSearchField({ onSearch, disabled }, ref) {
        const { t } = useTranslation();
        const [value, setValue] = useState('');
        const inputRef = useRef<HTMLInputElement>(null);

        useImperativeHandle(ref, () => ({
            reset: () => setValue(''),
            focus: () => inputRef.current?.focus(),
        }));

        // Focus the input as soon as it becomes enabled. This handles two cases:
        // 1. First open: isLoading is true on mount so the input starts disabled;
        //    this effect fires when loading finishes and disabled flips to false.
        // 2. Subsequent opens (data already cached): disabled is false from mount,
        //    so this effect focuses immediately.
        useEffect(() => {
            if (!disabled) {
                inputRef.current?.focus();
            }
        }, [disabled]);

        // Debounce: fire onSearch 300 ms after the user stops typing.
        useEffect(() => {
            const timer = setTimeout(() => {
                onSearch(value.trim().toLowerCase());
            }, 300);
            return () => clearTimeout(timer);
        }, [value, onSearch]);

        return (
            <TextField
                size="small"
                fullWidth
                disabled={disabled}
                autoComplete="off"
                placeholder={t('Filter deals or services')}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key !== 'Escape' && e.key !== 'Tab') e.stopPropagation();
                }}
                inputRef={inputRef}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'action.active' }} />
                            </InputAdornment>
                        ),
                    },
                }}
            />
        );
    },
);

const DealHeader = memo(function DealHeader({
    dealExternalId,
    dealName,
    dealClosingDate,
}: {
    dealExternalId: string;
    dealName: string;
    dealClosingDate: string | null;
}) {
    const { t } = useTranslation();
    return (
        <ListSubheader>
            {t('Deal')}: {dealExternalId} - {dealName}
            {dealClosingDate ? ` · ${dayjs(dealClosingDate).format('YYYY-MM-DD, HH:mm')}` : ''}
        </ListSubheader>
    );
});

// MenuItem outside of MUI Select context: no cloneElement overhead, no context subscription.
// memo is effective here — only re-renders when checked flips.
const ServiceRow = memo(
    forwardRef<HTMLLIElement, { serviceId: string; label: string; checked: boolean; onToggle: (id: string) => void }>(
        function ServiceRow({ serviceId, label, checked, onToggle }, ref) {
            return (
                <MenuItem ref={ref} dense disableRipple onClick={() => onToggle(serviceId)}>
                    <Checkbox checked={checked} size="small" disableRipple />
                    <ListItemText primary={label} slotProps={{ primary: { variant: 'body2' } }} />
                </MenuItem>
            );
        },
    ),
);

export const GroupedServiceSelect = memo(function GroupedServiceSelect({
    label,
    groups,
    selected,
    onChange,
    isLoading,
    autoOpen = false,
    onOpen,
}: {
    label: string;
    groups: DealGroupForSelector[];
    selected: string[];
    onChange: (ids: string[]) => void;
    isLoading: boolean;
    autoOpen?: boolean;
    onOpen?: () => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [appliedQuery, setAppliedQuery] = useState('');
    const searchFieldRef = useRef<ServiceSearchFieldHandle>(null);

    // Stable reference for the onSearch prop so the debounce effect in
    // ServiceSearchField doesn't re-fire when GroupedServiceSelect re-renders.
    const handleSearch = useCallback((q: string) => setAppliedQuery(q), []);

    // Ref so handleToggle never stales without becoming a new function reference.
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    const allServices = useMemo(() => groups.flatMap((g) => g.services), [groups]);
    const labelById = useMemo(() => new Map(allServices.map((s) => [s.id, serviceLabel(s)])), [allServices]);
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const filteredGroups = useMemo(
        () =>
            appliedQuery
                ? groups
                      .map((g) => {
                          // If the query matches the deal itself, show all its services.
                          const dealMatches =
                              g.dealName.toLowerCase().includes(appliedQuery) ||
                              g.dealExternalId.toLowerCase().includes(appliedQuery);
                          return {
                              ...g,
                              services: dealMatches
                                  ? g.services
                                  : g.services.filter((s) => serviceLabel(s).toLowerCase().includes(appliedQuery)),
                          };
                      })
                      .filter((g) => g.services.length > 0)
                : groups,
        [groups, appliedQuery],
    );

    // Stable reference — only onChange is a dep, not selected.
    const handleToggle = useCallback(
        (id: string) => {
            const curr = selectedRef.current;
            onChange(curr.includes(id) ? curr.filter((s) => s !== id) : [...curr, id]);
        },
        [onChange],
    );

    const anchorRef = useRef<HTMLDivElement>(null);
    const firstSelectedRef = useRef<HTMLLIElement>(null);

    const handleOpen = () => {
        setOpen(true);
        onOpen?.();
    };

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                firstSelectedRef.current?.scrollIntoView({ block: 'nearest' });
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Open the dropdown immediately when the component mounts (e.g. triggered by a button).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (autoOpen) handleOpen();
    }, []);

    const handleClose = () => {
        setOpen(false);
        searchFieldRef.current?.reset();
        setAppliedQuery('');
    };

    const displayValue = useMemo(
        () => (selected.length === 0 ? '' : selected.map((id) => labelById.get(id) ?? id).join(', ')),
        [selected, labelById],
    );

    return (
        <FormControl size="small" fullWidth>
            <InputLabel shrink={open || selected.length > 0} sx={open ? { color: 'primary.main' } : undefined}>
                {label}
            </InputLabel>
            <OutlinedInput
                ref={anchorRef}
                label={label}
                readOnly
                value={displayValue}
                notched={open || selected.length > 0}
                onClick={handleOpen}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpen();
                    }
                }}
                endAdornment={
                    <InputAdornment position="end">
                        <KeyboardArrowDownIcon
                            sx={{
                                transform: open ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                                color: 'action.active',
                                fontSize: 20,
                                mr: 0.5,
                            }}
                        />
                    </InputAdornment>
                }
                slotProps={{
                    input: {
                        tabIndex: 0,
                        'aria-haspopup': 'listbox',
                        'aria-expanded': open,
                        style: { cursor: 'pointer', caretColor: 'transparent' },
                    },
                }}
                sx={{
                    cursor: 'pointer',
                    ...(open && {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                        },
                    }),
                }}
            />
            <Popover
                open={open}
                anchorEl={anchorRef.current ?? undefined}
                onClose={handleClose}
                disableAutoFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                transitionDuration={0}
                slotProps={{
                    paper: {
                        sx: {
                            maxHeight: 320,
                            minWidth: anchorRef.current?.offsetWidth,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                }}
            >
                <Box sx={{ flex: 'none', p: 1 }}>
                    <ServiceSearchField ref={searchFieldRef} onSearch={handleSearch} disabled={isLoading} />
                </Box>
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                    <MenuList disablePadding>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : filteredGroups.length === 0 ? (
                            <MenuItem disabled>
                                <ListItemText
                                    primary={
                                        appliedQuery ? t('No services match your search') : t('No services available')
                                    }
                                    slotProps={{ primary: { variant: 'body2' } }}
                                />
                            </MenuItem>
                        ) : (
                            (() => {
                                let firstSelectedAttached = false;
                                return filteredGroups.flatMap((group) => [
                                    <DealHeader
                                        key={`hdr-${group.dealId}`}
                                        dealExternalId={group.dealExternalId}
                                        dealName={group.dealName}
                                        dealClosingDate={group.dealClosingDate}
                                    />,
                                    ...group.services.map((service) => {
                                        const isFirstSelected = !firstSelectedAttached && selectedSet.has(service.id);
                                        if (isFirstSelected) firstSelectedAttached = true;
                                        return (
                                            <ServiceRow
                                                key={service.id}
                                                ref={isFirstSelected ? firstSelectedRef : undefined}
                                                serviceId={service.id}
                                                label={serviceLabel(service)}
                                                checked={selectedSet.has(service.id)}
                                                onToggle={handleToggle}
                                            />
                                        );
                                    }),
                                ]);
                            })()
                        )}
                    </MenuList>
                </Box>
            </Popover>
        </FormControl>
    );
});
